import {
    AfterContentInit,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnInit,
    OnDestroy,
    signal,
    computed,
} from '@angular/core';
import { PlanwertDetail } from '../../domain/model/planwert-detail.model';
import { ActivatedRoute, Router } from '@angular/router';
import { WestnetzSidenavService } from '../../service/common/westnetz-sidenav/westnetz-sidenav.service';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { lastValueFrom, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ConfirmDialogService } from '../../service/common/dialog/confirm-dialog.service';
import { NotificationService } from '../../service/common/notification/notification.service';
import { PlanwertService } from '../../service/planwert/planwert.service';
import { PlanwertStatus, PlanwertStatusItem } from '../../domain/model/planwert-status.enum';
import {
    ResourceIdsSubFormComponent,
} from '../../erzeugungsanlage/erzeugungsanlage-form/resource-ids-sub-form/resource-ids-sub-form.component';
import {
    ErzeugungsanlageDetailFormComponent,
} from '../../erzeugungsanlage/erzeugungsanlage-form/erzeugungsanlage-detail-form/erzeugungsanlage-detail-form.component';
import {
    TechnischeResourceSubFormComponent,
} from '../../erzeugungsanlage/erzeugungsanlage-form/technische-resource-sub-form/technische-resource-sub-form.component';
import {
    TrancheSubFormComponent,
} from '../../erzeugungsanlage/erzeugungsanlage-form/tranche-sub-form/tranche-sub-form.component';
import {
    MarktlokationSubFormComponent,
} from '../../erzeugungsanlage/erzeugungsanlage-form/marktlokation-sub-form/marktlokation-sub-form.component';
import moment, { Moment } from 'moment';
import { ControlConfig } from '../../erzeugungsanlage/erzeugungsanlage-form/form-config.model';
import { Feld } from '../../domain/model/feld.enum';
import { LeistungsValidator } from '../../erzeugungsanlage/erzeugungsanlage-form/validator/leistungs.validator';
import { Erzeugungsanlage } from '../../domain/model/erzeugungsanlage';
import { PlanwertDetailEntry } from '../../domain/model/planwert-detail-entry.model';
import { ResourceIdValidator } from './validator/resource-id.validator';
import { PredicateValidator } from '../../erzeugungsanlage/erzeugungsanlage-form/validator/predicate.validator';
import { RoutingService } from '../../service/common/routing/routing.service';
import { TrancheGroesseValidator } from '../../erzeugungsanlage/erzeugungsanlage-form/validator/trancheGroesseValidator';

@Component({
    selector: 'redispatch-planwert-detail-form',
    templateUrl: './planwert-detail-form.component.html',
    styleUrls: [
        './planwert-detail-form.component.scss',
    ],
    standalone: false,
})
export class PlanwertDetailFormComponent implements OnInit, AfterContentInit, OnDestroy {

    public readonly PLANWERT_MIN_DATE: Moment = moment().add(1, 'days').startOf('day');

    private readonly ERROR_WRONG_DATE = { wrongDate: true };
    private readonly ERROR_MISSING_DATE = { dateRequired: true };
    private readonly ERROR_MIN_DATE = { matDatepickerMin: true };
    private readonly TRANCHE_FORM_GROUPS = [
        'trancheMarktlokation',
        'trancheBilanzkreis',
        'trancheLieferant',
        'tranchenGroesse',
    ];
    private readonly DATA_TRANSFER_DATE_FIELD_NAME = 'dataTransferDate';
    private readonly PLANNED_VALUE_FIELD_NAME = 'plannedValue';

    objectKeys = (o: object) => Object.keys(o || {});

    @Input()
    public selectedPlanwert: PlanwertDetail | undefined;

    @Input()
    public dokumentenId: string;

    @Input()
    public dataTransferDate: Date;

    @Input()
    public stwId: string;

    @Input()
    public planwert?: PlanwertDetail;

    @Input()
    public erzeugungsanlage: Erzeugungsanlage;

    @Input()
    public alreadyAllocatedDatesForDokumentId: Date[];

    @Input()
    public readonly = false;

    public Feld = Feld;

    public form: FormGroup = null;

    public showDeleteButton: boolean = false;
    public isSaveOrCreateButtonDisabled = computed(() => this.formInvalidOrPristine() || this.saveOrCreateButtonClicked());

    private formInvalidOrPristine = signal(true);
    private saveOrCreateButtonClicked = signal(false);
    private onDestroy$ = new Subject<void>();

    protected fb = inject(FormBuilder);
    private confirmDialogService = inject(ConfirmDialogService);
    private notificationService = inject(NotificationService);
    private activeRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private westnetzSidenavService = inject(WestnetzSidenavService);
    private planwertService = inject(PlanwertService);
    private routingService = inject(RoutingService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.createFormGroup();
        this.initializeFormWithMixedData();
        this.showDeleteButton = this.stwId &&
            this.stwId === this.erzeugungsanlage?.netzzuordnung?.netzbetreiber?.stadtwerkeId;

        if (this.readonly) {
            this.form.disable();
        }

        this.form.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.validateTrancheDateFields();
                this.changeDetectorRef.markForCheck();
            });

        this.form.statusChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(_value => {
                this.formInvalidOrPristine.set(this.form.invalid || this.form.pristine);
            });
    }

    ngAfterContentInit(): void {
        this.form.markAsDirty();
        this.form.markAllAsTouched();
        this.form.updateValueAndValidity();
        this.disableAllFieldsWherePlanwertIsNotOpenOrUnset();
        this.form.markAsPristine();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    /**
     * Initializes the form with mixed data (planwert or current state of erzeugungsanlage) by setting default values for each form control.
     * @returns void
     */
    private initializeFormWithMixedData(): void {
        this.form.patchValue({
            erzeugungsanlage: this.erzeugungsanlage,
            bruttonennleistung: this.initializePlanwertItem('bruttonennleistung', this.erzeugungsanlage.leistung?.bruttonennleistung),
            srId: this.initializePlanwertItem('srId', this.erzeugungsanlage.resourceIds?.steuerbareResourceId),
            sgId: this.initializePlanwertItem('sgId', this.erzeugungsanlage.resourceIds?.steuergruppeId),
            anlagebetreiberName: this.initializePlanwertItem('anlagebetreiberName', this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberName),
            anlagebetreiberStrasse: this.initializePlanwertItem('anlagebetreiberStrasse',
                this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberStrasse),
            anlagebetreiberHausnummer: this.initializePlanwertItem('anlagebetreiberHausnummer',
                this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberHausnummer),
            anlagebetreiberHausnummerZusatz: this.initializePlanwertItem('anlagebetreiberHausnummerZusatz',
                this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberHausnummerZusatz),
            anlagebetreiberPlz: this.initializePlanwertItem('anlagebetreiberPlz', this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberPLZ),
            anlagebetreiberOrt: this.initializePlanwertItem('anlagebetreiberOrt', this.erzeugungsanlage.technischeResourceInformationen?.anlagebetreiberOrt),
            marktlokation: this.initializePlanwertItem('marktlokation', this.erzeugungsanlage.marktlokation?.code),
            bilanzkreis: this.initializePlanwertItem('bilanzkreis', this.erzeugungsanlage.marktlokation?.bilanzkreis),
            lieferant: this.initializePlanwertItem('lieferant', this.erzeugungsanlage.marktlokation?.lieferant),
            trancheMarktlokation: this.initializePlanwertItem('trancheMarktlokation', this.erzeugungsanlage.tranche?.marktlokation),
            trancheBilanzkreis: this.initializePlanwertItem('trancheBilanzkreis', this.erzeugungsanlage.tranche?.bilanzkreis),
            trancheLieferant: this.initializePlanwertItem('trancheLieferant', this.erzeugungsanlage.tranche?.lieferant),
            tranchenGroesse: this.initializePlanwertItem('tranchenGroesse', this.erzeugungsanlage.tranche?.groesse),
        });
    }

    /**
     * Initializes a planwert item by setting its original value or returning a selected planwert.
     * @param formGroupName The name of the form group for the planwert item.
     * @param originalFieldValueOfErzeugungsanlage (Optional) The original field value of the erzeugungsanlage.
     * @returns The selected planwert, or a new planwert object with the original value.
     */
    private initializePlanwertItem(formGroupName: string, originalFieldValueOfErzeugungsanlage?: any): any {
        const selectedPlanwert = this.getPlanwertByFormGroupName(formGroupName);
        return selectedPlanwert ? this.mapPlanwertDtoToDomainModel(originalFieldValueOfErzeugungsanlage, selectedPlanwert) :
            {
                uuid: null,
                originalValue: originalFieldValueOfErzeugungsanlage,
                plannedValue: null,
                status: null,
                dataTransferDate: null,
                errorMessage: null,
            };
    }

    /**
     * Maps PlanwertDTO to internal domain model
     * @param currentValue The current value of the field we want to update with the planwert
     * @param planwert The PlanwertDTO which was received from backend
     * @returns The Planwert in the format in which it should be displayed
     */
    private mapPlanwertDtoToDomainModel(currentValue: any, planwert: PlanwertDetailEntry<any>): any {
        return {
            uuid: planwert.uuid,
            dataTransferDate: planwert.dataTransferDate,
            originalValue: currentValue,
            plannedValue: planwert.plannedValue,
            errorMessage: planwert.errorMessage,
            status: PlanwertStatusItem.getLabelByStatusEnum(planwert.status),
            statusHidden: PlanwertStatusItem.getLabelByStatusEnum(planwert.status),
        };
    }

    /**
     * Finds and returns planwert field by a given form group namme
     * @param formGroupName The name of the form group to clear the plannedValue field for.
     * @returns The requested planwert field
     */
    private getPlanwertByFormGroupName(formGroupName: string): PlanwertDetailEntry<any> {
        if (!this.planwert) {
            return null;
        }
        if (Object.keys(this.planwert).filter((key => formGroupName === key)).length != 1) {
            throw new Error(`Invalid field name (${formGroupName}) registered for selected form.`);
        }
        return this.planwert[formGroupName];
    }

    /**
     * This method returns the Netzverknuefungspunkt of a Erzeugungsanlage which is
     * a concated string out of the ortsname, stationsname and trafoFeldNummer
     */
    public getNetzverknuefungspunktCompactValue(): String {
        return [
            this.erzeugungsanlage.netzzuordnung?.ortsname,
            this.erzeugungsanlage.netzzuordnung?.stationsname,
            this.erzeugungsanlage.netzzuordnung?.trafoFeldNummer,
        ]
            .filter((nameElement) => nameElement !== null)
            .join(' ');
    }

    /**
     * This method clears a specific field of a specific form group name.
     * @param formGroupName The name of the form group
     */
    public clearFields(formGroupName: string): void {
        this.planwert[formGroupName] = null;
        this.getControl(formGroupName, 'plannedValue').setValue(null);
        this.getControl(formGroupName, 'status').setValue(null);
        this.getControl(formGroupName, 'dataTransferDate').setValue(null);
        this.getControl(formGroupName, 'errorMessage').setValue(null);
        this.form.markAsDirty();
        this.changeDetectorRef.markForCheck();
    }

    /**
     Checks if the specified form group has a planned value.
     @param {string} formGroupName - The name of the form group.
     @returns {boolean} Returns true if the form group has a planned value; otherwise, false.
     */
    public hasPlannedValue(formGroupName: string): boolean {
        return !!this.getControl(formGroupName, 'plannedValue').value;
    }

    /**
     Checks if the specified parent form group (if its tranches) has any planned value.
     @param {string} parentFormGroup - The name of the parent form group.
     @returns {boolean} Returns true if the parent form group is HEADER_TR_TRANCHE and has any tranche value; otherwise, false.
     */
    public hasTrancheAnyPlannedValue(parentFormGroup: string): boolean {
        if (parentFormGroup === Feld.HEADER_TR_TRANCHE) {
            return this.hasAnyTrancheValue;
        }
        return false;
    }

    /**
     * Checks if a specific form control is empty
     * @param formGroupName The name of the form group
     */
    public isInStatusOpenOrUnset(formGroupName: string): boolean {
        return this.getControl(formGroupName, 'status').value;
    }

    /**
     * Return the requested form control
     * @param formGroupName The name of the form group
     * @param fieldName The name of the field in the form group
     * @private
     */
    private getControl(formGroupName: string, fieldName: string) {
        return this.form.controls[formGroupName].get(fieldName);
    }

    /**
     * Initializes form group with validators
     */
    private createFormGroup(): void {
        this.form = this.fb.group({
            dataTransferDate: this.fb.control<Date>(null),
            bruttonennleistung: this.buildPlanwertDetailEntryForm<number>(
                ErzeugungsanlageDetailFormComponent.SHARED_BRUTTONENNLEISTUNG_VALIDATOR,
                {
                    max: 'Max. ' + LeistungsValidator.LEISTUNG_MAX_3_DECIMAL,
                    pattern: 'Max. 3 Nachkommastellen',
                },
            ),
            srId: this.buildPlanwertDetailEntryForm<string>(
                [
                    ResourceIdValidator.validate(this.erzeugungsanlage?.resourceIds?.steuerbareResourceId,
                        this.erzeugungsanlage?.resourceIds?.technischeResourceId),
                    ResourceIdsSubFormComponent.SHARED_SR_ID_VALIDATOR,
                ],
                {
                    checkDigit: 'Die Prüfsumme für die Steuerbare Ressource ID ist nicht korrekt.',
                    pattern: 'Länge 11, nur Großbuchstaben (wenn vorhanden), startet mit C und endet auf einer Zahl.',
                    missingIstWert: 'Bitte zuerst eine TR-ID zu der Erzeugungsanlage erfassen.',
                },
            ),
            sgId: this.buildPlanwertDetailEntryForm<string>(
                ResourceIdsSubFormComponent.SHARED_SG_ID_VALIDATOR,
                {
                    checkDigit: 'Die Prüfsumme für die Steuergruppen ID ist nicht korrekt.',
                    pattern: 'Länge 11, nur Großbuchstaben (wenn vorhanden), startet mit B und endet auf einer Zahl.',
                },
            ),
            anlagebetreiberName: this.buildPlanwertDetailEntryForm<string>(),
            anlagebetreiberStrasse: this.buildPlanwertDetailEntryForm<string>(),
            anlagebetreiberHausnummer: this.buildPlanwertDetailEntryForm<string>(),
            anlagebetreiberHausnummerZusatz: this.buildPlanwertDetailEntryForm<string>(),
            anlagebetreiberPlz: this.buildPlanwertDetailEntryForm<string>(
                TechnischeResourceSubFormComponent.SHARED_ANLAGENBETREIBER_PLZ_VALIDATOR,
                {
                    minlength: 'Postleitzahl zu kurz',
                    maxlength: 'Postleitzahl zu lang',
                    pattern: 'Bitte geben Sie eine fünfstellige Postleitzahl aus Deutschland ein',
                },
            ),
            anlagebetreiberOrt: this.buildPlanwertDetailEntryForm<string>(),
            marktlokation: this.buildPlanwertDetailEntryForm<string>(
                MarktlokationSubFormComponent.SHARED_MARKTLOKATION_VALIDATOR,
                {
                    pattern: 'Länge 11, nur Zahlen, startet mit einer Zahl(1-9).',
                    checkDigit: 'Die Prüfsumme für die Marktlokation ist nicht korrekt.',
                },
            ),
            bilanzkreis: this.buildPlanwertDetailEntryForm<string>(
                MarktlokationSubFormComponent.SHARED_BILANZKREIS_VALIDATOR,
                {
                    minlength: 'EIC zu kurz',
                    maxlength: 'EIC zu lang',
                    invalidCharacters: 'EIC enthält ungültige/s Zeichen',
                    checkCharInvalid: 'ID ungültig nach erfolgter Prüfzifferberechnung (Energy Identification Code)',
                },
            ),
            lieferant: this.buildPlanwertDetailEntryForm<string>(
                MarktlokationSubFormComponent.SHARED_LIEFERANT_VALIDATOR,
                {
                    pattern: 'Eine Lieferant-Marktlokation besteht aus 13 Zahlen und startet mit 40 oder 99',
                },
            ),
            trancheMarktlokation: this.buildPlanwertDetailEntryForm<string>(
                [
                    TrancheSubFormComponent.SHARED_MARKTLOKATION_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
                {
                    pattern: 'Länge 11, nur Zahlen, startet mit einer Zahl(1-9).',
                    checkDigit: 'Die Prüfsumme für die Marktlokation Tranche ist nicht korrekt.',
                    required: 'Es müssen alle oder keine Tranchenfelder befüllt werden!',
                    wrongDate: 'Alle Daten müssen gleich sein',
                    dateRequired: 'Datum muss Bruder',
                },
            ),
            trancheBilanzkreis: this.buildPlanwertDetailEntryForm<string>(
                [
                    TrancheSubFormComponent.SHARED_BILANZKREIS_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
                {
                    minlength: 'EIC zu kurz',
                    maxlength: 'EIC zu lang',
                    invalidCharacters: 'EIC enthält ungültige/s Zeichen',
                    checkCharInvalid: 'ID ungültig nach erfolgter Prüfzifferberechnung (Energy Identification Code)',
                    required: 'Es müssen alle oder keine Tranchenfelder befüllt werden!',
                    wrongDate: 'Alle Daten müssen gleich sein',
                },
            ),
            trancheLieferant: this.buildPlanwertDetailEntryForm<string>(
                [
                    TrancheSubFormComponent.SHARED_LIEFERANT_VALIDATOR,
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                ],
                {
                    pattern: 'Eine Lieferant Tranche besteht aus 13 Zahlen und startet mit 40 oder 99',
                    required: 'Es müssen alle oder keine Tranchenfelder befüllt werden!',
                    wrongDate: 'Alle Daten müssen gleich sein',
                },
            ),
            tranchenGroesse: this.buildPlanwertDetailEntryForm<number>(
                [
                    PredicateValidator.conditionalValidator(() => this.hasAnyTrancheValue, Validators.required),
                    ...TrancheGroesseValidator.validator2,
                ],
                {
                    max: '0 bis 100 und < 100 max. 2 Nachkommastellen',
                    pattern: '0 bis 100 und < 100 max. 2 Nachkommastellen',
                    required: 'Es müssen alle oder keine Tranchenfelder befüllt werden!',
                    wrongDate: 'Alle Daten müssen gleich sein',
                },
            ),
        });
    }


    private buildPlanwertDetailEntryForm<T>(
        plannedValueValidator: ValidatorFn | ValidatorFn[] = null,
        validationMessage: object = {},
    ): FormGroup {
        return this.fb.group({
            uuid: this.fb.control<string | undefined>({ value: null, disabled: false }),
            status: this.fb.control<PlanwertStatus>({ value: null, disabled: true }),
            statusHidden: this.fb.control<PlanwertStatus>({ value: null, disabled: false }),
            dataTransferDate: this.fb.control<Moment>({ value: null, disabled: false }),
            originalValue: this.fb.control<T | undefined>({ value: null, disabled: true }),
            plannedValue: this.fb.control<T | undefined>({ value: null, disabled: false }, plannedValueValidator),
            errorMessage: this.fb.control<string | undefined>({ value: null, disabled: true }),
            validationErrorMessage: validationMessage,
        });
    }

    /**
     Validates the date fields for the tranche.
     If any of the date fields has a different value, it sets the 'wrongDate' error for each field.
     In case it's not filled but the associated 'plannedValue' field is, it sets the '' error for the appropriate field.
     @private
     */
    private validateTrancheDateFields() {

        const trancheDateFields = this.getAbstractControlsByGroupAndField(this.TRANCHE_FORM_GROUPS, this.DATA_TRANSFER_DATE_FIELD_NAME);
        const trancheFields = this.getAbstractControlsByGroup(this.TRANCHE_FORM_GROUPS);

        this.resetErrorByFields(trancheDateFields);

        trancheFields.forEach(trancheField => {
            if (trancheField.get(this.PLANNED_VALUE_FIELD_NAME).value && !trancheField.get(this.DATA_TRANSFER_DATE_FIELD_NAME).value) {
                trancheField.get(this.DATA_TRANSFER_DATE_FIELD_NAME).setErrors(this.ERROR_MISSING_DATE);
            } else if (this.isFieldValueBeforeToday(trancheField, this.DATA_TRANSFER_DATE_FIELD_NAME)) {
                trancheField.get(this.DATA_TRANSFER_DATE_FIELD_NAME).setErrors(this.ERROR_MIN_DATE);
            } else {
                trancheField.get(this.DATA_TRANSFER_DATE_FIELD_NAME).setErrors(null);
            }
        });

        if (this.hasAnyDifferentDateValues) {
            trancheDateFields.forEach(field => field.setErrors(this.ERROR_WRONG_DATE));
        }
    }

    /**
     * Returns flag if field value is before today or not
     * @param field Passed form control
     * @param fieldName Name of form control (field) to be inspected
     * @private
     */
    private isFieldValueBeforeToday(field: AbstractControl<any, any>, fieldName: string): Boolean {
        return field.get(this.PLANNED_VALUE_FIELD_NAME).value
            && field.get(fieldName).value
            && new Date(field.get(fieldName).value).getTime() < new Date().getTime();
    }

    /**
     * Resets all error for given fields
     * @param fields Fields which error state will be reseted
     * @private
     */
    private resetErrorByFields(fields: AbstractControl<any, any>[]) {
        fields.forEach(field => field.setErrors(null));
    }

    /**
     * Returns a list of specific fields in a specific form group
     * @param groups The form group
     * @param field The requested form field
     * @return {Array<AbstractControl>} List of form controls (fields)
     */
    getAbstractControlsByGroupAndField(groups: string[], field: string): AbstractControl<any, any>[] {
        return this.getAbstractControlsByGroup(groups).map(fields => fields.get(field));
    }

    /**
     * Returns a list of form controls for specified groups
     * @param groups List of selected form groups
     * @return {Array<AbstractControl>} List of form controls (groups)
     */
    getAbstractControlsByGroup(groups: string[]): AbstractControl<any, any>[] {
        return groups.map(group => this.planwertItemFormControl(group));
    }

    /**
     Checks if the tranche has any different date values.
     @return {boolean} Returns true if there are different date values, otherwise false.
     */
    get hasAnyDifferentDateValues(): boolean {

        // in case there is already tranche data persisted, the validation logic isn't needed
        if (this.erzeugungsanlage.tranche) {
            return false;
        }

        return ![
            this.f.trancheMarktlokation.value.dataTransferDate,
            this.f.trancheBilanzkreis.value.dataTransferDate,
            this.f.trancheLieferant.value.dataTransferDate,
            this.f.tranchenGroesse.value.dataTransferDate,
        ]
            .filter(date => date)
            .map(date => new Date(date).toISOString())
            .every((val, _, arr) => val == arr[0]);
    }

    /**
     Checks if the tranche has any values.
     @return {boolean} Returns true if there are values, otherwise false.
     */
    get hasAnyTrancheValue(): boolean {

        // in case there is already tranche data persisted, the validation logic isn't needed
        if (this.erzeugungsanlage.tranche) {
            return false;
        }

        return !!this.f.trancheMarktlokation.value.plannedValue
            || !!this.f.trancheBilanzkreis.value.plannedValue
            || !!this.f.trancheLieferant.value.plannedValue
            || !!this.f.tranchenGroesse.value.plannedValue;

    }

    private disableAllFieldsWherePlanwertIsNotOpenOrUnset() {
        Object.keys(this.form.controls).filter(key => key !== 'dataTransferDate').forEach(key => {
            if (this.form.controls[key].value.statusHidden && this.form.controls[key].value.statusHidden !== 'Offen') {
                this.form.controls[key].disable();
            }
        });
    }

    public async createOrUpdatePlanwert() {
        try {
            this.saveOrCreateButtonClicked.set(true);
            await lastValueFrom(this.planwertService.createOrUpdatePlanwert(
                this.stwId,
                this.dokumentenId,
                this.preparePlanwertAsDTO(this.form.getRawValue()),
            ));
            await this.notificationService.success('Planwerte wurden gespeichert.');
            this.planwertService.requestRefresh();
            this.closeSideNav();
        } catch (e) {
            let errorTitle = 'Server Fehler';
            let errorMessage = 'Das hat leider nicht geklappt. Möglicherweise ist die Internetverbindung unterbrochen oder der Server hat ein Problem. ' +
                'Versuchen Sie bitte, den ' +
                'Vorgang nochmal zu wiederholen. Sollte es dann immer noch nicht funktionieren, kontaktieren Sie bitte ' +
                'Ihren Kundenbetreuer.';
            if (e.error && e.error.errors && e.error.message === 'There are one or more Planwerte with invalid IST-Werte') {
                const validationViolations = e.error.errors.map((error => error.fieldName));
                errorTitle = 'Ungültige Ist-Werte';
                errorMessage = `Ein oder mehrere Planwerte (${validationViolations}) enthalten ungültige Ist-Werte,
                die nicht mehr mit den aktuellen Werten der ausgewählten Erzeugungsanlage übereinstimmen.`;
            } else if (e.error && e.error.message === 'One or more planwerte already exist in this constellation.') {
                errorTitle = 'Planwertduplikate';
                errorMessage = 'Ein oder mehrere Planwerte existieren bereits in derselben Konstellation. Bitte prüfen und korrigieren Sie Ihre Eingaben.';
            } else if (e.error && e.error.message === 'Unable to create or update Planwert due invalid value') {
                errorTitle = 'Ungültige Planwerte oder invalide Erzeugungsanlage';
                errorMessage = `Ein oder mehrere Planwerte oder der Datenstand der ausgewählten Erzeugungsanlage sind invalide, was zu Fehlern führt.
<br/> Bitte kontaktieren sie den Support.`;
            } else if (e.error && e.error.message === 'Unable to create the planwert as there is already one in place for the same field in status \'OPEN\'') {
                errorTitle = 'Ungültige Eingabe';
                errorMessage = 'Es existiert bereits ein Planwert für diesen Feld im Status "offen". Bitte prüfen und korrigieren Sie Ihre Eingaben.';
            } else if (e.error && e.error.message === 'Unable to create the planwert as there is already one in place for ' +
                'the same field in status \'INVALID\'') {
                errorTitle = 'Ungültige Eingabe';
                errorMessage = 'Es existiert bereits ein Planwert für diesen Feld im Status "ungültig". Bitte prüfen und korrigieren Sie Ihre Eingaben.';
            }
            if (await this.confirmDialogService.confirm(
                errorMessage,
                errorTitle,
                'warn',
                'OK',
                null)) {
                this.saveOrCreateButtonClicked.set(false);
            }
        }
    }

    /**
     * This function
     *
     * @param planwert The PlanwertDetail object to be prepared.
     * @return The modified PlanwertDetail object.
     */
    private preparePlanwertAsDTO(planwert: PlanwertDetail): PlanwertDetail {

        // set all "Planwerte" without SOLL-Werte to null
        Object.keys(planwert).forEach(key => {
            if (planwert.hasOwnProperty(key)) {
                const fieldValue = planwert[key];

                if (typeof fieldValue === 'object' && fieldValue !== null && 'plannedValue' in fieldValue) {
                    if ((fieldValue['plannedValue'] === null || fieldValue['plannedValue'].length <= 0)
                        && (fieldValue['status'] === null || fieldValue['status'].length <= 0 || fieldValue['status'] === 'open')) {
                        planwert[key] = null;
                    }
                }
            }
        });

        // if not already done set the status of all "Planwerte" to 'OPEN'
        Object.keys(planwert)
            .filter(key => key !== 'dataTransferDate')
            .filter(key => (planwert[key] as PlanwertDetailEntry<any>))
            .map(key => planwert[key] as PlanwertDetailEntry<any>)
            .forEach(planwertDetail => {
                if (planwertDetail && planwertDetail.status === null) {
                    planwertDetail.status = PlanwertStatus.OPEN;
                } else if (planwertDetail && planwertDetail.status) {
                    planwertDetail.status = PlanwertStatusItem.getStatusEnumByLabel(planwertDetail.status);
                }
            });

        Object.keys(planwert)
            .filter(key => (key == 'bruttonennleistung' || key == 'tranchenGroesse') && planwert[key] != null)
            .map(key => planwert[key] as PlanwertDetailEntry<number>)
            .map(val =>
                val.plannedValue = Number((val.plannedValue?.toString().replace(',', '.'))),
            );

        return planwert;

    }

    public async deletePlanwert() {
        if (await this.confirmDialogService.confirm(
            'Möchten Sie alle Planwerte zu dieser Erzeugungsanlage löschen?',
            'Planwerte löschen',
            'warn',
            'Löschen')) {
            await lastValueFrom(this.planwertService.deletePlanwerte(this.stwId, this.dokumentenId));
            await this.notificationService.success('Ausgewählter Planwerte wurde gelöscht.');
            this.planwertService.requestRefresh();
            this.westnetzSidenavService.closeRightnav();
            this.router.navigate([
                this.routingService.planwertPath(),
            ],
            { queryParams: { erzeugungsanlageDokumentenId: this.erzeugungsanlage.dokumentenId } });
        }
    }

    public resetForm() {
        this.planwertService.getPlanwerte(this.dokumentenId, this.stwId)
            .pipe(take(1))
            .subscribe(planwert => {
                this.planwert = planwert;
                this.initializeFormWithMixedData();
                this.form.markAsPristine();
                this.changeDetectorRef.markForCheck();
            });
    }

    public closeSideNav() {
        this.westnetzSidenavService.closeRightnav();
        this.router.navigate([], {
            relativeTo: this.activeRoute,
            queryParams: { erzeugungsanlage: null, date: null, createnewplannedvalue: null },
            queryParamsHandling: 'merge',
        });
    }

    public get f(): ControlConfig<PlanwertDetail> {
        return this.form.controls as ControlConfig<PlanwertDetail>;
    }

    public planwertItemFormControl(currentGroup: string) {
        // ngOnInit must be done, because of initialisation of form controls and groups
        return this.form.controls['' + currentGroup];
    }
}
